import {
  IProvidedTranslationProps,
  withTranslations,
} from '@wix/wixstores-client-common-components/dist/es/src/outOfIframes/translations';
import classNames from 'classnames';
import React, {useEffect, useRef} from 'react';
import {BoxSelection, BoxSelectionBoxSize} from 'wix-ui-tpa/cssVars';
import {UserInputType} from '../../../constants';
import {useGlobals, withGlobalProps} from '../../../providers/globalPropsProvider';
import {scrollAndFocusUserInput} from '../utils/scrollAndFocusUserInput';
import s from './OptionsButtons.scss';
import {classes as boxSelectionStyles} from './OptionsButtons.st.css';
import {VIEW_MORE_OPTIONS_URL_FRAGMENT} from '@wix/wixstores-client-storefront-sdk/dist/src/constants';

export interface OptionsButtonsProps extends IProvidedTranslationProps {
  optionIndex: number;
}

export enum OptionsButtonsDataHook {
  Container = 'options-buttons-container',
  Buttons = 'options-buttons',
  Title = 'options-buttons-title',
}

const OptionsButtonsComponent: React.FC<OptionsButtonsProps> = ({optionIndex, t}) => {
  const globals = useGlobals();
  const buttonContainerRef = useRef<HTMLDivElement>(null);

  const {
    product,
    userInputs,
    variantInfo,
    isBackInStockEnabled,
    handleUserInput,
    validateUserInputsChanges,
    scrollAndFocusInputTarget,
    isQuickView,
    isProductSubmitted,
    userInputErrors,
  } = globals;

  useEffect(() => {
    if (!scrollAndFocusInputTarget || !buttonContainerRef.current) {
      return;
    }

    const isScrollAndFocusInputTarget =
      UserInputType.Selection === scrollAndFocusInputTarget?.inputType &&
      optionIndex === scrollAndFocusInputTarget?.index;
    /* istanbul ignore else */
    if (isScrollAndFocusInputTarget) {
      scrollAndFocusUserInput(UserInputType.Selection, buttonContainerRef.current, isQuickView);
    }
  }, [scrollAndFocusInputTarget, optionIndex, isQuickView]);

  const productOption = product?.options?.[optionIndex];

  const handleChange = ({id: selectionId}: {id: string}) => {
    const nextSelection = productOption.selections.find(({id}) => id.toString() === selectionId);

    handleUserInput(UserInputType.Selection, nextSelection, optionIndex);
    validateUserInputsChanges();
  };

  const activeSelection = userInputs?.[UserInputType.Selection]?.[optionIndex];

  const getSelectionAvailabilityInfo = (selectionId: number) => {
    return variantInfo.selectionsAvailability[productOption.id][selectionId];
  };

  const getVisibleSelections = () => {
    return productOption.selections
      .filter((selection) => getSelectionAvailabilityInfo(selection.id).isVisible)
      .map((selection) => {
        const availabilityInfo = getSelectionAvailabilityInfo(selection.id);
        return {
          ...selection,
          isSelectable: isBackInStockEnabled ? true : availabilityInfo.isSelectable,
        };
      });
  };

  const getBoxSelectionOption = () => {
    const options = getVisibleSelections();
    return options.map((selection) => (
      <BoxSelection.Option
        key={selection.id}
        id={selection.id.toString()}
        unstableHasInnerWrapper={false}
        checked={activeSelection?.id === selection.id}
        aria-label={selection.description}
        data-hook={`box-selection-option-${selection.id}`}
        className={classNames(boxSelectionStyles.optionItem, s.buttonOptionItemContainer)}
        disabled={!selection.isSelectable}>
        <div
          data-hook={`box-selection-text-option-${selection.id}`}
          className={classNames(s.optionItemText, {
            [s.disabled]: !selection.isSelectable,
            [s.selected]: activeSelection?.id === selection.id,
          })}>
          {selection.description}
        </div>
      </BoxSelection.Option>
    ));
  };

  const {title} = productOption;
  const hasError = isProductSubmitted && userInputErrors?.[UserInputType.Selection]?.[optionIndex];
  return (
    <div
      data-hook={OptionsButtonsDataHook.Container}
      id={`${VIEW_MORE_OPTIONS_URL_FRAGMENT}-${optionIndex}`}
      ref={buttonContainerRef}>
      <BoxSelection
        required
        aria-required
        label={title}
        name={`options-buttons-${optionIndex}`}
        aria-label={title}
        size={BoxSelectionBoxSize.xSmall}
        onChange={handleChange}
        className={classNames(boxSelectionStyles.optionItem, s.fitOptionItem, {
          [s.quickView]: isQuickView,
        })}
        data-hook={OptionsButtonsDataHook.Buttons}
        error={hasError}
        errorMessage={t('SELECT_OPTION_WARNING', {optionTitle: title})}>
        {getBoxSelectionOption()}
      </BoxSelection>
    </div>
  );
};

export const OptionsButtons: React.FC<OptionsButtonsProps> = withGlobalProps(
  withTranslations('globals.texts')(OptionsButtonsComponent)
);
